import {
  LocationsService,
  OrganizationMeetingLocationResponse,
  OrganizationMeetingLocationType,
} from "client/openapi";
import type { LocationBooking } from "client/openapi/models/LocationBooking";
import moment from "moment";
import { ReactNode, useEffect, useState } from "react";
import { formatTimeRange } from "util/time";
import TimeGrid, {
  Block,
  Column,
  GridPanel,
  minutesInto,
  overlaps,
} from "./TimeGrid";

/** Rooms shown at a time; the arrows page through the rest. */
const PAGE = 5;

/**
 * The meeting's day for the organization's rooms (in-person places), five
 * side by side with every booking, so free times stand out (in the calendar
 * pop-up, every room in a row that scrolls sideways). Bookings show their
 * name only where the viewer's own calendar would show it; the rest read
 * "Booked". Free time is white and bookings gray. The arrows beside the date look at other
 * days; clicking a room picks it and the time clicked (in 15-minute steps),
 * on the day shown.
 */
export default function RoomsGrid({
  orgId,
  locations,
  start,
  duration,
  locationId,
  noLocationYet = false,
  editingMeetingId,
  columnsInView,
  empty,
  onPick,
  close,
}: {
  orgId: number;
  locations: OrganizationMeetingLocationResponse[];
  /** The meeting being booked: its start, length and place. */
  start: string;
  duration: number;
  locationId?: number;
  /**
   * Book Meeting before any place is chosen: "Choose a location to see rooms"
   * shows in place of the rooms.
   */
  noLocationYet?: boolean;
  editingMeetingId?: number;
  /** The calendar pop-up: every room, this many in view, no arrows. */
  columnsInView?: number;
  /** Shown under "Choose a location to see rooms": Book Meeting's Location. */
  empty?: ReactNode;
  onPick: (
    location: OrganizationMeetingLocationResponse,
    start: string
  ) => void;
  /** Only in the calendar pop-up, where the grid takes the form's place. */
  close?: { label: string; onClick: () => void };
}) {
  const meetingDayKey = moment(start).format("YYYY-MM-DD");
  // The day shown: the meeting's, until the arrows move it.
  const [dayKey, setDayKey] = useState(meetingDayKey);
  useEffect(() => setDayKey(meetingDayKey), [meetingDayKey]);
  const day = moment(dayKey);
  const showDay = (days: number) =>
    setDayKey(day.clone().add(days, "days").format("YYYY-MM-DD"));
  const [bookings, setBookings] = useState<LocationBooking[]>();
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  // Rooms are the in-person places.
  const rooms = locations.filter(
    (location) =>
      location.location_type === OrganizationMeetingLocationType.IN_PERSON
  );
  const pageOf = (id?: number) =>
    Math.max(0, Math.floor(rooms.findIndex((room) => room.id === id) / PAGE));
  const [page, setPage] = useState(() => pageOf(locationId));

  // A newly chosen room brings its page into view.
  useEffect(() => {
    if (locationId !== undefined) setPage(pageOf(locationId));
    // Only a new room moves the page; the arrows move it otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (noLocationYet) return;
    let cancelled = false;
    setBookings(undefined);
    setFailed(false);
    const from = moment(dayKey);
    LocationsService.getLocationBookings({
      orgId,
      start: from.toISOString(),
      until: from.clone().add(1, "day").toISOString(),
      excludeMeetingId: editingMeetingId,
    })
      .then((result) => {
        if (!cancelled) setBookings(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dayKey, orgId, editingMeetingId, noLocationYet, reload]);

  const thisStart = moment(start);
  // The meeting's time of day, on whichever day is shown.
  const thisFrom = minutesInto(moment(meetingDayKey), thisStart);
  const thisMeeting = {
    from: thisFrom,
    to: Math.min(24 * 60, thisFrom + duration),
  };
  const shown = columnsInView
    ? rooms
    : rooms.slice(page * PAGE, page * PAGE + PAGE);

  const columns: Column[] = shown.map((location) => {
    const blocks: Block[] = (bookings ?? [])
      .filter((booking) => booking.location_id === location.id)
      .map((booking, i) => {
        const from = moment(booking.start);
        const to = moment(booking.end);
        return {
          id: i,
          label: `${booking.label ?? "Booked"}, ${formatTimeRange(
            from.toDate(),
            to.toDate()
          )}`,
          from: minutesInto(day, from),
          to: minutesInto(day, to),
        };
      })
      .sort((a, b) => a.from - b.from);
    const busy = overlaps(thisMeeting, blocks);
    const address = [
      location.address_line_1,
      location.address_line_2,
      location.city,
      [location.state, location.zip].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
    return {
      key: String(location.id),
      title: location.name,
      subtitle: location.address_line_1,
      hint: [location.name, address].filter(Boolean).join("\n"),
      badge: busy
        ? { text: "Busy", tone: "busy" }
        : { text: "Free", tone: "free" },
      lanes: [{ key: "room", blocks }],
      thisMeeting:
        location.id === locationId && dayKey === meetingDayKey
          ? { ...thisMeeting, clash: busy }
          : undefined,
      pickTitle: `Pick ${location.name}`,
    };
  });

  const paged = !columnsInView && rooms.length > PAGE;
  return (
    <GridPanel
      label={day.format("ddd, MMM D")}
      previous={{ label: "Previous day", onClick: () => showDay(-1) }}
      next={{ label: "Next day", onClick: () => showDay(1) }}
      pager={
        paged
          ? {
              label: `Rooms ${page * PAGE + 1}–${
                page * PAGE + shown.length
              } of ${rooms.length}`,
              previous: {
                label: "Previous rooms",
                onClick: () => setPage(page - 1),
                disabled: page === 0,
              },
              next: {
                label: "Next rooms",
                onClick: () => setPage(page + 1),
                disabled: (page + 1) * PAGE >= rooms.length,
              },
            }
          : undefined
      }
      roomLegend
      failed={failed}
      failedText="Could not load bookings."
      onRetry={() => setReload(reload + 1)}
      close={close}
    >
      {noLocationYet ? (
        empty ? (
          <div className="py-3">
            <p className="mb-2 text-sm text-gray-500">
              Choose a location to see rooms
            </p>
            {empty}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-gray-500">
            Choose a location to see rooms
          </p>
        )
      ) : rooms.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          This organization has no rooms yet.
        </p>
      ) : (
        <TimeGrid
          columns={columns}
          loading={!bookings}
          // Opens about an hour above the meeting's time.
          focusMinutes={thisFrom}
          columnsInView={columnsInView}
          hoverMinutes={duration}
          onPick={(column, minutes) => {
            const location = rooms.find(
              (room) => String(room.id) === column.key
            );
            // Setting minutes past 59 rolls into the hours on the clock.
            if (location) {
              onPick(location, day.clone().minutes(minutes).format());
            }
          }}
        />
      )}
    </GridPanel>
  );
}
